import { useState } from 'react';
import type { HardwareAsset, OrgRole } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';

import { Link as RouterLink } from '@tanstack/react-router';

import { useAuthStore } from '../../auth/store.js';
import { useT } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { useFleet } from './use-fleet.js';
import { HardwareAssetModal } from './HardwareAssetModal.js';
import { KIND_LABEL, MODALITY_LABEL, STATUS_META } from './labels.js';

/** L'écriture (ajouter/modifier/retirer un matériel) exige EDITOR+ ; la lecture est ouverte à tous. */
const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

/** État d'édition : `undefined` = fermé, `null` = création, un matériel = édition. */
type Editing = HardwareAsset | null | undefined;

/**
 * Section « Flotte » des matériels (ADR-0029, incrément 1). Grille des matériels de l'organisation ;
 * chaque carte se **dérive** d'un `HardwareAsset` (§5 : le rendu est piloté par la donnée, une refonte
 * UX ne rouvre pas le contrat). L'ajout et l'édition passent par la fiche (rôle éditeur).
 *
 * `readOnly` : sur l'**Overview**, on ne fait que **lister** et on renvoie vers la page **Deploy**, où
 * s'opère l'ajout/gestion. Ailleurs (Deploy), la section est pleinement éditable.
 */
export function FleetSection({
  readOnly = false,
  anchorId = 'overview-fleet',
}: {
  readOnly?: boolean;
  anchorId?: string;
} = {}) {
  const t = useT();
  const { assets, loading, error, create, update, remove } = useFleet();
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canManage = !readOnly && role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;

  const [editing, setEditing] = useState<Editing>(undefined);

  return (
    <section className="ddp-fleet">
      <div className="ddp-overview__section-head">
        <h2 id={anchorId} className="ddp-overview__subtitle">
          {t('fleet.title')}
          {assets.length > 0 && <span className="ddp-fleet__count"> · {assets.length}</span>}
          <HelpHint textKey="help.overview.fleet" />
        </h2>
        {readOnly ? (
          // Overview : lecture seule, on renvoie vers la page dédiée pour ajouter/gérer.
          <RouterLink to="/deploy" hash="deploy-fleet" className="ddp-overview__seeall">
            {t('fleet.manage')}
          </RouterLink>
        ) : (
          canManage && (
            <Button kind="ghost" size="sm" renderIcon={Add} onClick={() => setEditing(null)}>
              {t('fleet.add')}
            </Button>
          )
        )}
      </div>

      <p className="ddp-fleet__lead">{t('fleet.lead')}</p>

      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {!loading && assets.length === 0 ? (
        canManage ? (
          // Carte de création (comme la tuile « Autres images ») : le vide devient une action. Le
          // bouton « Ajouter un matériel » en tête reste le second chemin.
          <div className="ddp-fleet__grid">
            <button type="button" className="ddp-more-card" onClick={() => setEditing(null)}>
              <span className="ddp-more-card__plus" aria-hidden="true">
                0
              </span>
              <span className="ddp-more-card__label">{t('fleet.title')}</span>
              <span className="ddp-more-card__hint">{t('fleet.add')}</span>
            </button>
          </div>
        ) : (
          <p className="ddp-overview__empty">{t('fleet.empty')}</p>
        )
      ) : (
        <div className="ddp-fleet__grid">
          {assets.map((asset) => (
            <FleetCard
              key={asset.id}
              asset={asset}
              interactive={canManage}
              onOpen={() => setEditing(asset)}
            />
          ))}
        </div>
      )}

      {editing !== undefined && (
        <HardwareAssetModal
          asset={editing}
          onClose={() => setEditing(undefined)}
          onSubmit={(input) => (editing ? update(editing.id, input) : create(input))}
          onDelete={editing ? () => remove(editing.id) : undefined}
        />
      )}
    </section>
  );
}

/** Une carte de matériel, dérivée de la donnée. Les specs « rapides » viennent de l'enveloppe calcul. */
function FleetCard({
  asset,
  interactive,
  onOpen,
}: {
  asset: HardwareAsset;
  interactive: boolean;
  onOpen: () => void;
}) {
  const t = useT();
  const status = STATUS_META[asset.status];
  const computeBadge = asset.compute.soc ?? asset.compute.accelerator ?? null;
  const specs = [
    asset.compute.modality ? t(MODALITY_LABEL[asset.compute.modality]) : null,
    asset.compute.sensorFeed ?? null,
    asset.compute.inputResolution ?? null,
  ].filter((s): s is string => Boolean(s));

  const body = (
    <>
      <div className="ddp-fleet-card__top">
        <span className="ddp-fleet-card__kind">{t(KIND_LABEL[asset.kind])}</span>
        <span className={`ddp-status ddp-status--${status.cls}`}>{t(status.key)}</span>
      </div>
      <div className="ddp-fleet-card__name">{asset.name}</div>
      {asset.reference && <div className="ddp-fleet-card__ref">{asset.reference}</div>}
      <div className="ddp-fleet-card__specs">
        {computeBadge && (
          <span className="ddp-fleet-card__spec ddp-fleet-card__spec--compute">{computeBadge}</span>
        )}
        {specs.map((s) => (
          <span key={s} className="ddp-fleet-card__spec">
            {s}
          </span>
        ))}
      </div>
      <div className="ddp-fleet-card__foot">
        {asset.components.length} {t('fleet.card.componentsUnit')}
      </div>
    </>
  );

  if (!interactive) {
    return <div className="ddp-fleet-card">{body}</div>;
  }
  return (
    <button type="button" className="ddp-fleet-card ddp-fleet-card--btn" onClick={onOpen}>
      {body}
    </button>
  );
}
